import React, { useMemo, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useGetDealerBySapDropdownListQuery } from '../../../../api/endpoints/dealer';

const CURRENCY_OPTIONS = ['TRY', 'EUR', 'USD'];

const EMPTY_FILTERS = { dealerCode: null, year: null, currency: null };

const CollectionReportFilters = ({ onApply, onReset, isDealerOnly }) => {
    const { th } = useLanguage();
    const [open, setOpen] = useState(true);
    const [dealerSearch, setDealerSearch] = useState('');
    const [localFilters, setLocalFilters] = useState(EMPTY_FILTERS);

    const { data: dealerList = [], isLoading: isLoadingDealers } = useGetDealerBySapDropdownListQuery(
        { RowCount: 9999, SearchText: dealerSearch },
        { skip: isDealerOnly }
    );

    const dealerOptions = useMemo(
        () =>
            (dealerList || [])
                .filter((d) => d?.sapDealerId != null)
                .map((d) => {
                    const code = String(d.sapDealerId);
                    const name = d.name ?? d.translatedText ?? '';
                    return { label: name ? `${code} - ${name}` : code, value: code };
                }),
        [dealerList]
    );

    const yearOptions = useMemo(() => {
        const current = new Date().getFullYear();
        const years = [];
        for (let y = current + 1; y >= current - 5; y -= 1) {
            years.push({ label: String(y), value: y });
        }
        return years;
    }, []);

    const currencyOptions = useMemo(() => CURRENCY_OPTIONS.map((c) => ({ label: c, value: c })), []);

    const setField = (field, value) => setLocalFilters((prev) => ({ ...prev, [field]: value }));

    const handleApply = () => onApply(localFilters);

    const handleReset = () => {
        setLocalFilters(EMPTY_FILTERS);
        setDealerSearch('');
        onReset();
    };

    return (
        <div className="generic-table-filter mb-2">
            <div className="grid">
                <div className="col-12 md:col-6 flex justify-content-start">
                    <Button
                        icon="pi pi-filter"
                        label={th('filter.advancedFilter') || 'Filtrele'}
                        onClick={() => setOpen((p) => !p)}
                        outlined={!open}
                        style={{ minWidth: '120px' }}
                        type="button"
                    />
                </div>
            </div>

            {open && (
                <div className="mt-3">
                    <Divider />
                    <div className="text-xl font-medium mb-3">{th('filter.advancedFilters') || 'Filtreler'}</div>

                    <div className="grid">
                        {!isDealerOnly && (
                            <div className="col-12 md:col-6 lg:col-3">
                                <div className="field mb-2 p-1">
                                    <label htmlFor="collectionReportDealer" className="block mb-1 text-sm">
                                        {th('collectionReport.dealer') || 'Bayi'}
                                    </label>
                                    <Dropdown
                                        id="collectionReportDealer"
                                        value={localFilters.dealerCode}
                                        options={dealerOptions}
                                        onChange={(e) => setField('dealerCode', e.value || null)}
                                        onFilter={(e) => setDealerSearch(e.filter)}
                                        placeholder={th('collectionReport.dealerPlaceholder') || 'Tüm bayiler'}
                                        className="w-full"
                                        filter
                                        showClear
                                        loading={isLoadingDealers}
                                        virtualScrollerOptions={{ itemSize: 38 }}
                                        appendTo="self"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="col-12 md:col-6 lg:col-3">
                            <div className="field mb-2 p-1">
                                <label htmlFor="collectionReportYear" className="block mb-1 text-sm">
                                    {th('collectionReport.year') || 'Yıl'}
                                </label>
                                <Dropdown
                                    id="collectionReportYear"
                                    value={localFilters.year}
                                    options={yearOptions}
                                    onChange={(e) => setField('year', e.value ?? null)}
                                    placeholder={th('collectionReport.yearPlaceholder') || 'Tüm yıllar'}
                                    className="w-full"
                                    showClear
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6 lg:col-3">
                            <div className="field mb-2 p-1">
                                <label htmlFor="collectionReportCurrency" className="block mb-1 text-sm">
                                    {th('collectionReport.currency') || 'Para Birimi'}
                                </label>
                                <Dropdown
                                    id="collectionReportCurrency"
                                    value={localFilters.currency}
                                    options={currencyOptions}
                                    onChange={(e) => setField('currency', e.value || null)}
                                    placeholder={th('collectionReport.currencyPlaceholder') || 'Tümü'}
                                    className="w-full"
                                    showClear
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-content-start mt-3">
                        <Button
                            label={th('filter.reset') || 'Sıfırla'}
                            icon="pi pi-refresh"
                            className="p-button-outlined mr-2"
                            onClick={handleReset}
                            type="button"
                        />
                        <Button
                            label={th('filter.apply') || 'Uygula'}
                            icon="pi pi-check"
                            onClick={handleApply}
                            type="button"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CollectionReportFilters;
